import { Injectable, inject, signal, computed } from '@angular/core';
import { HttpClient, HttpInterceptorFn, HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { CanActivateFn, Router } from '@angular/router';
import { Observable, of, from, tap, catchError, switchMap, throwError } from 'rxjs';

export interface UsuarioSesion {
  id: string;
  empresaId: string;
  nombre: string;
  rol: string;
}

interface RespuestaLogin {
  token: string;
  usuario: UsuarioSesion;
}

const CLAVE_TOKEN = 'crm_token';
const CLAVE_USUARIO = 'crm_usuario';

const USUARIO_VISTA_DEFECTO: UsuarioSesion = {
  id: '00000000-0000-0000-0000-000000000001',
  empresaId: '00000000-0000-0000-0000-0000000000eb',
  nombre: 'Paula Herrera',
  rol: 'admin'
};

let cacheSnapshotPromise: Promise<any> | null = null;

function obtenerSnapshotReal(): Promise<any> {
  if (!cacheSnapshotPromise) {
    cacheSnapshotPromise = fetch('/api-snapshot.json')
      .then(r => (r.ok ? r.json() : null))
      .catch(() => null);
  }
  return cacheSnapshotPromise;
}

function resolverDesdeSnapshot(urlCompleta: string, metodo: string, snap: any): any {
  if (!snap) return null;
  const u = new URL(urlCompleta, window.location.origin);
  const path = u.pathname;

  if (metodo !== 'GET') {
    if (path.endsWith('/api/auth/login')) {
      return { token: 'modo-lectura-cloudflare', usuario: snap.usuarioSesion ?? USUARIO_VISTA_DEFECTO };
    }
    return { id: '00000000-0000-0000-0000-000000000000', ok: true };
  }

  if (path === '/api/usuarios') {
    return snap.usuarios ?? [];
  }
  if (path === '/api/embudos') {
    return snap.embudos ?? [];
  }

  const mTablero = path.match(/^\/api\/embudos\/([^/]+)\/tablero$/);
  if (mTablero) {
    const embId = mTablero[1].toLowerCase();
    const est = (u.searchParams.get('estado') || 'abierto').toLowerCase();
    const tableros = snap.tableros ?? {};
    return tableros[`${embId}:${est}`] ?? tableros[`${mTablero[1]}:${est}`] ?? [];
  }

  if (path === '/api/pendientes') {
    const est = (u.searchParams.get('estado') || 'abierto').toLowerCase();
    return snap.pendientes?.[est] ?? snap.pendientes?.abierto ?? [];
  }

  if (path === '/api/contactos') {
    const q = (u.searchParams.get('q') || '').trim().toLowerCase();
    const lista: any[] = snap.contactos ?? [];
    if (!q) return lista;
    return lista.filter(c =>
      `${c.nombres ?? ''} ${c.apellidos ?? ''} ${c.numeroDocumento ?? ''} ${c.telefono ?? ''}`
        .toLowerCase()
        .includes(q)
    );
  }

  const mCitas = path.match(/^\/api\/contactos\/([^/]+)\/citas$/);
  if (mCitas) {
    const cid = mCitas[1].toLowerCase();
    const citasMap = snap.citas ?? {};
    return citasMap[cid] ?? citasMap[mCitas[1]] ?? { citas: [], aviso: null };
  }

  const mFicha = path.match(/^\/api\/contactos\/([^/]+)$/);
  if (mFicha) {
    const cid = mFicha[1].toLowerCase();
    const fichasMap = snap.fichas ?? {};
    return fichasMap[cid] ?? fichasMap[mFicha[1]] ?? null;
  }

  return [];
}

@Injectable({ providedIn: 'root' })
export class SesionService {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);

  readonly token = signal<string | null>(this.inicializarToken());
  readonly usuario = signal<UsuarioSesion | null>(this.leerUsuarioGuardado());
  readonly autenticado = computed(() => !!this.token() && !!this.usuario());

  private inicializarToken(): string {
    const guardado = localStorage.getItem(CLAVE_TOKEN);
    if (guardado) return guardado;
    localStorage.setItem(CLAVE_TOKEN, 'modo-lectura-cloudflare');
    return 'modo-lectura-cloudflare';
  }

  private leerUsuarioGuardado(): UsuarioSesion {
    const crudo = localStorage.getItem(CLAVE_USUARIO);
    if (crudo) {
      try {
        return JSON.parse(crudo) as UsuarioSesion;
      } catch {
        // fallback al usuario de vista por defecto
      }
    }
    localStorage.setItem(CLAVE_USUARIO, JSON.stringify(USUARIO_VISTA_DEFECTO));
    return USUARIO_VISTA_DEFECTO;
  }

  iniciarSesion(correo: string, clave: string) {
    return this.http.post<RespuestaLogin>('/api/auth/login', { correo, clave }).pipe(
      tap((r) => {
        localStorage.setItem(CLAVE_TOKEN, r.token);
        localStorage.setItem(CLAVE_USUARIO, JSON.stringify(r.usuario));
        this.token.set(r.token);
        this.usuario.set(r.usuario);
      })
    );
  }

  cerrarSesion() {
    localStorage.removeItem(CLAVE_TOKEN);
    localStorage.removeItem(CLAVE_USUARIO);
    this.token.set(null);
    this.usuario.set(null);
    this.router.navigateByUrl('/acceso');
  }
}

export const interceptorAutenticacion: HttpInterceptorFn = (req, next) => {
  const sesion = inject(SesionService);
  const t = sesion.token();
  const esHostEstatico =
    typeof window !== 'undefined' &&
    window.location.hostname !== 'localhost' &&
    window.location.hostname !== '127.0.0.1';

  const servirSnapshot = (): Observable<HttpResponse<any>> =>
    from(obtenerSnapshotReal()).pipe(
      switchMap((snap) => {
        const body = resolverDesdeSnapshot(req.urlWithParams, req.method, snap);
        return of(new HttpResponse({ status: 200, body }));
      })
    );

  // En Cloudflare Pages (o cualquier dominio externo) servimos directamente la fotografía real
  if (esHostEstatico && req.url.startsWith('/api/')) {
    return servirSnapshot();
  }

  const peticion = t && t !== 'modo-lectura-cloudflare' && !req.url.endsWith('/api/auth/login')
    ? req.clone({ setHeaders: { Authorization: `Bearer ${t}` } })
    : req;

  return next(peticion).pipe(
    catchError((err: HttpErrorResponse) => {
      if (req.url.startsWith('/api/')) {
        return servirSnapshot();
      }
      return throwError(() => err);
    })
  );
};

export const guardiaSesion: CanActivateFn = () => {
  const sesion = inject(SesionService);
  const router = inject(Router);
  return sesion.autenticado() ? true : router.createUrlTree(['/acceso']);
};
